// The tools wall (after Clay's "Trusted by…" panel): a dark band, then a light rounded
// panel with three full-width rows of tiles sliding in opposite directions: the apps
// our products connect to, plain facts, and workflows written in one sentence.
// Every tile states something true; there are no customer logos or quotes here until
// there are real ones to show.
import type { ReactNode } from "react";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { SmartLink } from "@/components/Header";
import { integrations, GET_STARTED_URL } from "@/data/site";
import { Rise } from "./motion";

const remote = (slug: string) => `https://logos.composio.dev/api/${slug}`;

type Tile =
  | { kind: "app"; name: string; logo: string; href?: string }
  | { kind: "fact"; big: string; small: string }
  | { kind: "line"; label: string; text: string; wide?: boolean };

const local = integrations.map<Tile>((i) => ({ kind: "app", name: i.name, logo: i.logo, href: i.href }));
const more = (list: [string, string][]): Tile[] => list.map(([slug, name]) => ({ kind: "app", name, logo: remote(slug) }));

const ROWS: Tile[][] = [
  [
    local[0],
    { kind: "fact", big: "3 products", small: "One login opens them all" },
    local[1],
    { kind: "line", label: "A workflow, in one sentence", text: "When a customer emails, summarise it and post it to #support.", wide: true },
    local[2],
    local[3],
    { kind: "fact", big: "About 1 minute", small: "To connect an app with its own sign-in" },
    local[4],
    local[5],
    local[6],
  ],
  [
    ...more([["salesforce", "Salesforce"], ["jira", "Jira"]]),
    { kind: "fact", big: "0 bots", small: "Join your calls with ClearMeet" },
    ...more([["github", "GitHub"], ["zendesk", "Zendesk"]]),
    { kind: "line", label: "An agent, in one sentence", text: "Answer visitors from our website and help documents.", wide: true },
    ...more([["shopify", "Shopify"], ["linear", "Linear"], ["asana", "Asana"]]),
    { kind: "fact", big: "1 click", small: "Disconnects any app, any time" },
    ...more([["airtable", "Airtable"], ["trello", "Trello"]]),
  ],
  [
    ...more([["zoom", "Zoom"], ["googledrive", "Google Drive"]]),
    { kind: "line", label: "A reminder, in one sentence", text: "If Acme has not replied in 3 days, send a friendly reminder.", wide: true },
    ...more([["googlesheets", "Google Sheets"], ["intercom", "Intercom"]]),
    { kind: "fact", big: "Free", small: "To start, on every product" },
    ...more([["stripe", "Stripe"], ["calendly", "Calendly"], ["dropbox", "Dropbox"], ["clickup", "ClickUp"]]),
    { kind: "fact", big: "Thousands", small: "More apps in the catalogue" },
  ],
];

const TileView = ({ t }: { t: Tile }) => {
  if (t.kind === "app") {
    const inner = (
      <>
        <img src={t.logo} alt={`${t.name} logo`} referrerPolicy="no-referrer" loading="lazy" className="h-8 w-8 object-contain opacity-80 grayscale transition-[filter,opacity] duration-300 group-hover:opacity-100 group-hover:grayscale-0" />
        <span className="text-[18px] font-semibold tracking-[-0.01em] text-foreground">{t.name}</span>
        {t.href && <ArrowUpRight className="absolute right-3 top-3 h-4 w-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />}
      </>
    );
    const cls = "group relative flex h-[104px] w-[230px] shrink-0 items-center justify-center gap-3 rounded-2xl bg-background shadow-[0_1px_2px_hsl(0_0%_0%/0.05)] transition-shadow hover:shadow-[0_14px_30px_-18px_hsl(0_0%_0%/0.35)]";
    return t.href ? <SmartLink href={t.href} className={cls}>{inner}</SmartLink> : <div className={cls}>{inner}</div>;
  }
  if (t.kind === "fact") {
    return (
      <div className="flex h-[104px] shrink-0 items-center gap-4 rounded-2xl bg-background px-6 shadow-[0_1px_2px_hsl(0_0%_0%/0.05)]">
        <span className="whitespace-nowrap text-[26px] font-semibold tracking-[-0.03em] text-[hsl(216_84%_48%)]">{t.big}</span>
        <span className="w-[150px] text-[12.5px] font-medium uppercase leading-tight tracking-[0.04em] text-[hsl(0_0%_35%)]">{t.small}</span>
      </div>
    );
  }
  return (
    <div className={`flex h-[104px] shrink-0 flex-col justify-center rounded-2xl bg-background px-6 shadow-[0_1px_2px_hsl(0_0%_0%/0.05)] ${t.wide ? "w-[430px]" : "w-[300px]"}`}>
      <span className="text-[12px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">{t.label}</span>
      <span className="mt-1.5 text-[15.5px] leading-snug text-foreground">&ldquo;{t.text}&rdquo;</span>
    </div>
  );
};

const Row = ({ tiles, reverse, speed }: { tiles: Tile[]; reverse?: boolean; speed: number }) => (
  <div className="cm-marquee overflow-hidden">
    <div className={`cm-marquee-track flex w-max gap-3 pr-3 ${reverse ? "cm-marquee-rev" : ""}`} style={{ ["--cm-marquee-s" as string]: `${speed}s` }}>
      {tiles.map((t, i) => <TileView key={i} t={t} />)}
      <div aria-hidden className="flex gap-3">
        {tiles.map((t, i) => <TileView key={`d${i}`} t={t} />)}
      </div>
    </div>
  </div>
);

export const ToolsWall = ({ children }: { children?: ReactNode }) => (
  <section aria-labelledby="tools-h" className="relative overflow-hidden bg-[linear-gradient(180deg,hsl(216_84%_50%),hsl(222_72%_22%)_75%)]">
    <div aria-hidden className="cm-grid-dark pointer-events-none absolute inset-x-0 top-0 h-[520px]" />
    <div className="relative mx-auto max-w-[1200px] px-4 pb-20 pt-24 text-center sm:px-8 sm:pt-32">
      <Rise>
        <h2 className="mx-auto max-w-4xl text-[2.6rem] font-semibold leading-[1.02] tracking-[-0.04em] text-[hsl(0_0%_100%)] sm:text-[4.4rem]">
          Start with one product. Add the rest when you are ready.
        </h2>
        <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <SmartLink href={GET_STARTED_URL} className="group inline-flex h-12 items-center gap-2 rounded-xl bg-[hsl(0_0%_100%)] px-6 text-[16px] font-semibold text-[hsl(0_0%_4%)] transition-colors hover:bg-[hsl(0_0%_4%)] hover:text-[hsl(0_0%_100%)]">
            Get started free <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </SmartLink>
          <SmartLink href="/products" className="inline-flex h-12 items-center rounded-xl border border-[hsl(0_0%_100%/0.25)] px-6 text-[16px] font-medium text-[hsl(0_0%_100%)] transition-colors hover:bg-[hsl(0_0%_100%/0.08)]">
            See all products
          </SmartLink>
        </div>
      </Rise>
    </div>
    {children}
    <div className="relative px-2 pb-2 sm:px-6 sm:pb-6">
      <div className="mx-auto max-w-[1840px] rounded-[28px] bg-[hsl(216_50%_95%)] py-14 sm:rounded-[40px] sm:py-20">
        <Rise className="px-6 text-center">
          <h3 id="tools-h" className="mx-auto max-w-3xl text-[1.6rem] font-medium leading-snug tracking-[-0.02em] text-foreground sm:text-[2rem]">
            Built on the tools your team <span className="font-semibold">already trusts</span>. Nothing to move, <span className="font-semibold">nothing new to learn</span>.
          </h3>
        </Rise>
        <div className="mt-12 space-y-3">
          <Row tiles={ROWS[0]} speed={70} />
          <Row tiles={ROWS[1]} speed={80} reverse />
          <Row tiles={ROWS[2]} speed={75} />
        </div>
        <div className="mt-10 text-center">
          <SmartLink href="/integrations" className="group inline-flex items-center gap-1.5 text-[16px] font-semibold text-[hsl(216_84%_45%)]">
            See every app <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </SmartLink>
        </div>
      </div>
    </div>
  </section>
);
